import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import jwt_decode from 'jwt-decode';

import {
  MENU_HOME,
  MENU_LED,
  MENU_LED_DELIVERY_SPECIALIST,
  MENU_LED_JIT,
  MENU_LED_RETAILER_PERFORMANCE,
  MENU_LOVE_PROMISE,
  MENU_LP_CORPORATE_REPORT_DOWNLOAD,
  MENU_LP_HOME_PAGE,
  MENU_LP_RETAILER_SUMMARY_REPORT,
  MENU_OLP_LEGACY,
  MENU_OLP_LEGACY_EXTERNAL,
  MENU_REPORTING,
  MENU_RP_COMMENT,
  MENU_RP_JUST_IN_TIME,
  MENU_RP_MY_REPORTS,
  MENU_SCORES,
  MENU_SCORES_SNAPSHOT,
  MENU_SCORES_TREND,
  MENU_SUPPORT,
} from '../../shared/constants/menu.const';
import { Navigation } from '../../models';
import {
  AuthService,
  EcaSetupConfigurationService,
  SetupConfigAttribute,
} from '@drc-eca/eca-components-lib';
import { environment } from '../../../../../../../environments/environment';
import { filter, take, tap } from 'rxjs/operators';
import { UserService } from '@services/user.service';
import {
  ROUTE_LED_DELIVERY_SPECIALIST,
  ROUTE_LED_JIT,
  ROUTE_LED_RETAILER_PERFORMANCE,
  ROUTE_LP_CORPORATE_REPORT,
  ROUTE_LP_HOME_PAGE,
  ROUTE_LP_RETAILER_REPORT,
  ROUTE_LP_SUPPORT_DOCUMENTS,
  ROUTE_REPORTS_COMMENTS,
  ROUTE_REPORTS_JIT,
  ROUTE_REPORTS_LANDING,
  ROUTE_REPORTS_SNAPSHOT,
  ROUTE_REPORTS_TREND,
} from '../../shared/constants/routes.const';
import { ConfigurationService } from '@services/configuration.service';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
})
export class NavComponent implements OnInit {
  logoAlt = 'Horizontal blue oval with silver stars with SUBARU beneath Subaru logo';
  navigators: Array<Navigation> = [];
  expandedIndex = -1;
  subExpandedIndex = -1;
  currentLocation: any;
  sideNavMode: any = 'side';
  navState: any = {
    opened: true,
    mobileView: false,
    closeNav: function () {},
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private configurationService: ConfigurationService,
    private userService: UserService,
  ) {
    if (window.innerWidth <= 1529) {
      this.navState.opened = false;
      this.sideNavMode = 'over';
      this.navState.mobileView = true;
      this.route.params.subscribe(() => {
        this.navState.opened = false;
      });
    }
    this.navState.closeNav = this.closeSidebar;
    // this.navigators.push(new Navigation(MENU_LOVE_PROMISE,'', undefined, true));
  }

  public async ngOnInit() {
    this.configurationService
      .getAttribute('NextGenUI:IR:external-links-map')
      .pipe(
        take(1),
        tap((externalLinks: SetupConfigAttribute) => {
          this.currentLocation = this.router;
          if (this.userService.userIsCorporateUser) {
            const { promiseHomeNav, promiseRetailerNav, promiseCorporateNav } =
              this.addRoutesForCorporateUser(externalLinks);

            if (
              [promiseHomeNav.route, promiseRetailerNav.route, promiseCorporateNav.route].includes(
                this.router.url,
              )
            ) {
              this.expandedIndex = 2;
            }
          } else if (this.userService.userIsDeliverySpecialist) {
            this.addRoutesForDeliverySpecialist(externalLinks);
          } else if (this.userService.userIsExternalUser) {
            this.addRoutesForExternalUser(externalLinks);
          }

          this.navigators.forEach((navigator, navIndex) => {
            navigator.child.forEach((childNav) => {
              if (this.router.url === childNav.route) {
                this.expandedIndex = navIndex;
              }
            });
          });
        }),
      )
      .subscribe();
  }

  private addRoutesForDeliverySpecialist(externalLinks: SetupConfigAttribute): void {
    const ledNav: Navigation[] = [];
    this.addLEDJITRoute(ledNav);
    this.addDeliverySpecialistRoute(ledNav);

    this.navigators.push(new Navigation(MENU_LED, '/', ledNav));

    const promiseNav = new Array<Navigation>();
    this.addLovePromiseHomePageRoute(promiseNav);
    this.addLovePromiseRetailerReportRoute(promiseNav);
    this.navigators.push(new Navigation(MENU_LOVE_PROMISE, '/', promiseNav));
    this.addLegacyOLPLink(externalLinks);
    this.addLovePromiseSupportDocsRoute();
  }

  private addRoutesForExternalUser(externalLinks: SetupConfigAttribute): void {
    const ledNav: Navigation[] = [];
    this.addLEDJITRoute(ledNav);

    const promiseNav = new Array<Navigation>();
    this.addLovePromiseHomePageRoute(promiseNav);
    this.addLovePromiseRetailerReportRoute(promiseNav);

    this.addLEDBaseRoute(ledNav);
    this.addLovePromiseBaseRoute(promiseNav);
    this.addLegacyOLPLink(externalLinks);
    this.navigators.push(new Navigation(MENU_SUPPORT, ROUTE_LP_SUPPORT_DOCUMENTS));
  }

  private addRoutesForCorporateUser(externalLinks: SetupConfigAttribute) {
    const reportNav = this.addNewOlpRoutes();

    const scoresNav: Navigation[] = [];
    this.addScoresRoutes(scoresNav, reportNav);

    const ledNav: Navigation[] = [];
    this.addLEDJITRoute(ledNav);
    this.addLEDRetailerPerformanceRoute(ledNav);
    this.addDeliverySpecialistRoute(ledNav);
    this.addOLPBaseRoute(reportNav);
    this.addLEDBaseRoute(ledNav);
    const promiseNav = new Array<Navigation>();
    const promiseHomeNav = this.addLovePromiseHomePageRoute(promiseNav);
    const promiseRetailerNav = this.addLovePromiseRetailerReportRoute(promiseNav);
    const promiseCorporateNav = this.addLovePromiseCorporateReportRoute(promiseNav);

    this.addLovePromiseBaseRoute(promiseNav);
    this.addLegacyOLPLink(externalLinks);
    this.addLovePromiseSupportDocsRoute();
    return { promiseHomeNav, promiseRetailerNav, promiseCorporateNav };
  }

  private addLegacyOLPLink(externalLinks: SetupConfigAttribute): void {
    this.navigators.push(
      new Navigation(MENU_OLP_LEGACY, externalLinks.value['OLP LEGACY'], undefined, true),
    );
  }

  private addLovePromiseSupportDocsRoute(): void {
    this.navigators.push(new Navigation(MENU_SUPPORT, ROUTE_LP_SUPPORT_DOCUMENTS));
  }

  private addLovePromiseHomePageRoute(promiseNav: Navigation[]): Navigation {
    const promiseHomeNav = new Navigation(MENU_LP_HOME_PAGE, ROUTE_LP_HOME_PAGE);
    promiseNav.push(promiseHomeNav);
    return promiseHomeNav;
  }

  private addDeliverySpecialistRoute(ledNav: Navigation[]): void {
    ledNav.push(new Navigation(MENU_LED_DELIVERY_SPECIALIST, ROUTE_LED_DELIVERY_SPECIALIST));
  }

  private addLEDJITRoute(ledNav: Navigation[]): void {
    ledNav.push(new Navigation(MENU_LED_JIT, ROUTE_LED_JIT));
  }

  private addLEDBaseRoute(ledNav: Navigation[]): void {
    this.navigators.push(new Navigation(MENU_LED, '/', ledNav));
  }

  private addLEDRetailerPerformanceRoute(ledNav: Navigation[]): void {
    ledNav.push(new Navigation(MENU_LED_RETAILER_PERFORMANCE, ROUTE_LED_RETAILER_PERFORMANCE));
  }

  private addLovePromiseBaseRoute(promiseNav: Navigation[]): void {
    this.navigators.push(new Navigation(MENU_LOVE_PROMISE, '/', promiseNav));
  }

  private addLovePromiseRetailerReportRoute(promiseNav: Navigation[]): Navigation {
    const promiseRetailerNav = new Navigation(
      MENU_LP_RETAILER_SUMMARY_REPORT,
      ROUTE_LP_RETAILER_REPORT,
    );
    promiseNav.push(promiseRetailerNav);
    return promiseRetailerNav;
  }

  private addOLPBaseRoute(reportNav: Navigation[]): void {
    this.navigators.push(new Navigation(MENU_REPORTING, '/', reportNav));
  }

  private addLovePromiseCorporateReportRoute(promiseNav: Navigation[]): Navigation {
    const promiseCorporateNav = new Navigation(
      MENU_LP_CORPORATE_REPORT_DOWNLOAD,
      ROUTE_LP_CORPORATE_REPORT,
    );
    promiseNav.push(promiseCorporateNav);
    return promiseCorporateNav;
  }

  private addScoresRoutes(scoresNav: Navigation[], reportNav: Navigation[]) {
    scoresNav.push(new Navigation(MENU_SCORES_SNAPSHOT, ROUTE_REPORTS_SNAPSHOT));
    scoresNav.push(new Navigation(MENU_SCORES_TREND, ROUTE_REPORTS_TREND));
    reportNav.push(new Navigation(MENU_SCORES, '/', scoresNav));
  }

  private addNewOlpRoutes(): Navigation[] {
    const reportNav = new Array<Navigation>();
    reportNav.push(new Navigation(MENU_HOME, ROUTE_REPORTS_LANDING));
    reportNav.push(new Navigation(MENU_RP_JUST_IN_TIME, ROUTE_REPORTS_JIT));
    reportNav.push(new Navigation(MENU_RP_COMMENT, ROUTE_REPORTS_COMMENTS));
    return reportNav;
  }

  public closeSidebar(): void {
    this.navState.opened = false;
  }

  public handleLinkEnter(route): void {
    window.open(route, 'blank');
  }

  public handleInternalLinkEnter(route): void {
    this.router.navigate([route]);
  }

  public expandPanel(panelIndex: number): void {
    setTimeout(() => {
      this.expandedIndex = panelIndex;
    }, 0);
  }
}
